// Input gate screen (the `input_gate` step). Twin of the desktop InputGatePage.
// The participant must type the expected code to continue. There is no decline
// or cancel path: the only way forward is the correct text.
//
// Behaviour (parity with input_gate_page.py):
//   - full-screen background_color (default the desktop block default #2d3748)
//   - centered prompt, a text field, and a Submit button
//   - comparison is CASE-SENSITIVE and exact (no trimming)
//   - correct  -> button locks to "Unlocked", then submit { passed: true }
//   - incorrect-> show the error message, clear the field, refocus it
//   - Enter submits; Escape does nothing (cannot dismiss the gate)
//   - re-entrance guard so a fast double-submit cannot advance twice
//
// Submit payload: { passed: true }.

import { useEffect, useRef, useState, type KeyboardEvent } from "react";
import type { WebRendererProps } from "./registry";

const DEFAULT_BG = "#2d3748"; // desktop block_default_bg
const DEFAULT_PROMPT = "Enter the code to continue:";
const DEFAULT_ERROR =
  "Incorrect. Please do not touch anything, wait for researcher instruction.";

export default function InputGateRenderer({ step, onDone }: WebRendererProps) {
  const settings = (step.settings ?? {}) as Record<string, unknown>;
  const expected = typeof settings.expected_input === "string" ? settings.expected_input : "";
  const promptText =
    typeof settings.prompt_text === "string" && settings.prompt_text
      ? settings.prompt_text
      : DEFAULT_PROMPT;
  const errorMessage =
    typeof settings.error_message === "string" && settings.error_message
      ? settings.error_message
      : DEFAULT_ERROR;
  const bg =
    typeof settings.background_color === "string" && settings.background_color
      ? settings.background_color
      : DEFAULT_BG;
  const pauseAfter =
    typeof settings.pause_after_seconds === "number" ? settings.pause_after_seconds : 0;
  // Hide what's typed (dots) when the researcher enabled it - parity with the
  // desktop InputGatePage's Password echo mode.
  const mask = settings.mask_input === true;

  const [value, setValue] = useState("");
  const [showError, setShowError] = useState(false);
  const [locked, setLocked] = useState(false);
  const lockedRef = useRef(false);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  function validate() {
    if (lockedRef.current) return; // re-entrance guard
    if (value.trim() === expected.trim()) {
      // Trim leading/trailing whitespace before matching, so a stray space
      // doesn't silently break the gate - parity with the desktop InputGatePage.
      lockedRef.current = true;
      setLocked(true);
      setShowError(false);
      const submit = () => onDone({ kind: "submit", result: { passed: true } });
      if (pauseAfter > 0) window.setTimeout(submit, pauseAfter * 1000);
      else submit();
    } else {
      setShowError(true);
      setValue("");
      inputRef.current?.focus();
    }
  }

  function onKeyDown(e: KeyboardEvent<HTMLInputElement>) {
    if (e.key === "Enter") {
      e.preventDefault();
      validate();
    }
    // Escape is intentionally ignored: the gate cannot be dismissed.
  }

  return (
    <div className="jol-gate" style={{ background: bg }}>
      <div className="jol-gate-prompt">{promptText}</div>
      <input
        ref={inputRef}
        type={mask ? "password" : "text"}
        className="jol-gate-input"
        value={value}
        disabled={locked}
        onChange={(e) => setValue(e.target.value)}
        onKeyDown={onKeyDown}
      />
      <button type="button" className="jol-gate-submit" disabled={locked} onClick={validate}>
        {locked ? "Unlocked" : "Submit"}
      </button>
      {showError ? <div className="jol-gate-error">{errorMessage}</div> : null}
    </div>
  );
}
